import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Avatar,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import { MasterListData } from "../../types/List";
import { imageBaseUrl } from "../../api/Index";
import { initialUserData, UserData } from "../../types/User";
import { PatientData } from "../../types/Patient";
import { masterListEnum } from "../../utils/AppEnum";

interface ComponentProps {
  onSubmit: (user: Partial<UserData>) => void;
  onClose: () => void;
  initialData?: Partial<UserData>;
  genderData: MasterListData[];
}

const PatientForm: React.FC<ComponentProps> = ({
  onSubmit,
  onClose,
  initialData,
  genderData,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState<Partial<UserData>>(
    initialData || initialUserData
  );
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [previewImage, setPreviewImage] = useState<string>("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      setForm(initialData);
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setForm((prev: Partial<PatientData>) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  function fileToBase64(file: File): Promise<{ base64: string; mime: string }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => {
        reader.abort();
        reject(new DOMException("Problem reading file."));
      };
      reader.onload = () => {
        const result = reader.result as string; // data:[mime];base64,AAAA...
        // optionally strip data URL prefix:
        const match = result.match(/^data:(.+);base64,(.*)$/);
        if (!match) return reject(new Error("Invalid file data"));
        resolve({ mime: match[1], base64: match[2] });
      };
      reader.readAsDataURL(file);
    });
  }

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.firstName) newErrors.firstName = "First name is required";
    if (!form.lastName) newErrors.lastName = "Last name is required";
    if (!form.email) newErrors.email = "Email is required";
    if (!form.genderId) newErrors.gender = "Gender is required";
    if (!form.dateOfBirth) newErrors.dateOfBirth = "Date of birth is required";
    if (!form.primaryPhone)
      newErrors.primaryPhone = "Primary phone is required";
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    const { base64, mime } = file
      ? await fileToBase64(file)
      : { base64: null, mime: null };
    // optional: limit client-side size check (in bytes)
    if (file != null) {
      const maxBytes = 5 * 1024 * 1024; // 5 MB
      if (file.size > maxBytes) throw new Error("File too large (max 5 MB)");
    }

    form.profileImageBase64 = base64; // only the base64 content
    form.profileImageMime = mime; // e.g. "image/png"
    onSubmit(form);
  };

  useEffect(() => {
    if (form.profileImagePath) {
      setImageUrl(`${imageBaseUrl}${form.profileImagePath}`);
    }
  }, [form.profileImagePath]);

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
      {/* 🔹 Responsive grid layout */}
      <Box
        display="grid"
        gridTemplateColumns={isMobile ? "1fr" : "repeat(2, 1fr)"}
        gap={2}
      >
        <Box>
          <TextField
            name="firstName"
            label="First Name"
            value={form.firstName}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.firstName}
            helperText={errors.firstName}
          />

          <TextField
            name="middleName"
            label="Middle Name"
            value={form.middleName}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />

          <TextField
            name="lastName"
            label="Last Name"
            value={form.lastName}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.lastName}
            helperText={errors.lastName}
          />

          <TextField
            name="email"
            label="Email"
            value={form.email}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.email}
            helperText={errors.email}
          />

          <TextField
            name="dateOfBirth"
            label="Date of Birth"
            type="date"
            value={
              form.dateOfBirth
                ? new Date(form.dateOfBirth).toISOString().split("T")[0]
                : ""
            }
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.dateOfBirth}
            helperText={errors.dateOfBirth}
            InputLabelProps={{
              shrink: true,
            }}
            inputProps={{
              max: new Date().toISOString().split("T")[0],
            }}
          />
          <TextField
            select
            name="genderId"
            label="Gender"
            value={form.genderId}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.genderId}
            helperText={errors.genderId}
          >
            <MenuItem key={0} value={0}>
              Please select
            </MenuItem>
            {genderData
              .filter((x) => x.masterListId == masterListEnum.Gender)
              .map((data) => (
                <MenuItem key={data.id} value={data.id}>
                  {data.name}
                </MenuItem>
              ))}
          </TextField>
        </Box>

        {/* RIGHT SIDE: Other Fields */}
        <Box>
          <TextField
            name="primaryPhone"
            label="Primary Phone"
            value={form.primaryPhone}
            onChange={handleChange}
            fullWidth
            margin="normal"
            error={!!errors.primaryPhone}
            helperText={errors.primaryPhone}
          />

          <TextField
            name="secondaryPhone"
            label="Secondary Phone"
            value={form.secondaryPhone}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />

          <TextField
            name="nationalIdNumber"
            label="National ID No."
            value={form.nationalIdNumber}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />

          {form.id != null && form.id > 0 && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive ?? true}
                  onChange={handleChange}
                  name="isActive"
                />
              }
              label="Active"
            />
          )}
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            mb={2}
          >
            {previewImage ? (
              <Avatar
                src={previewImage}
                alt="Preview"
                sx={{
                  width: 120,
                  height: 120,
                  mb: 1,
                  boxShadow: 3,
                  border: "2px solid #1976d2",
                }}
              />
            ) : imageUrl ? (
              // ✅ Show saved image from API
              <Avatar
                src={imageUrl}
                alt="User Profile"
                sx={{
                  width: 120,
                  height: 120,
                  mb: 1,
                  boxShadow: 3,
                  border: "2px solid #1976d2",
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  mb: 1,
                  bgcolor: "grey.300",
                  boxShadow: 1,
                }}
              />
            )}
            <Button variant="outlined" component="label">
              Upload Image
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            {form.profileImagePath && (
              <Typography variant="caption" color="text.secondary" mt={1}>
                Image selected
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Action buttons */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap={2}
        marginTop={3}
      >
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          fullWidth
        >
          Close
        </Button>
        <Button type="submit" variant="contained" color="success" fullWidth>
          Save
        </Button>
      </Box>
    </form>
  );
};

export default PatientForm;
